import { THEME_PREFERENCES, type ThemePreference } from '@mg/shared';

/**
 * The theme rule and this browser's copy of the choice (My Profile > Appearance).
 *
 * The per-user value lives on the server (`User.themePreference`) and follows the user across devices;
 * the browser keeps a copy under one key only so the very first paint already has the right theme. The
 * inline script at the top of apps/web/index.html applies the SAME rule before any of this loads, and
 * theme/headScript.spec.ts runs that script against these functions for every case, so the two cannot
 * drift apart unnoticed.
 *
 * The `mg.` prefix predates the Smorgas Social name and stays, like the other `mg.*` keys: it is an
 * internal id, and moving a key would silently reset everyone's choice.
 */
export const THEME_STORAGE_KEY = 'mg.theme';

/** The media query System follows. */
export const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

/** What is on screen: the `dark` class on <html>, or not. */
export type ResolvedTheme = 'light' | 'dark';

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/** This browser's copy. LIGHT when there is none, when it is not one of the three values, or when storage is blocked. */
export function readStoredPreference(): ThemePreference {
  try {
    // The property access itself is inside the try: some browsers throw a SecurityError on
    // `window.localStorage` when site data is blocked. No `window` at all (a static render) lands here too.
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'LIGHT';
  } catch {
    return 'LIGHT';
  }
}

/** Keep this browser's copy. Blocked storage (private mode, site data off) is not an error: the theme still applies, it is just not remembered here. */
export function writeStoredPreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* blocked storage */
  }
}

/** Whether the operating system asks for dark. False without matchMedia (old browsers, jsdom, a static render). */
export function systemPrefersDark(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia(SYSTEM_DARK_QUERY).matches;
  } catch {
    return false;
  }
}

/** Light unless told otherwise: DARK is always dark, SYSTEM is dark only when the OS is. */
export function resolveTheme(preference: ThemePreference, osDark: boolean): ResolvedTheme {
  return preference === 'DARK' || (preference === 'SYSTEM' && osDark) ? 'dark' : 'light';
}

/**
 * Paint it: the `dark` class on <html> (the CSS variables in index.css do the rest) and `color-scheme`,
 * so native controls and scrollbars match.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}
